"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useTranslations } from "next-intl";

import TrackedWebAppLink from "./tracked-web-app-link";

const SENTINEL_ID = "homepage-differentiator-start";

const StickyMobileCta: React.FC = () => {
  const t = useTranslations("hero");
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = document.getElementById(SENTINEL_ID);
    if (!sentinel) {
      return;
    }

    const updateVisibility = () => {
      const { top } = sentinel.getBoundingClientRect();
      // Only show after the sentinel has scrolled above the viewport — not
      // when it simply sits below the fold on initial load.
      setVisible(top < 0);
    };

    updateVisibility();

    const observer = new IntersectionObserver(
      () => {
        updateVisibility();
      },
      { threshold: 0 },
    );

    observer.observe(sentinel);
    window.addEventListener("scroll", updateVisibility, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", updateVisibility);
    };
  }, []);

  // Chrome iOS hides its browser controls during scroll. `window.innerHeight`
  // stays fixed while `visualViewport.height` grows beyond it, so the offset
  // becomes negative — meaning the element must be pushed *down* past the
  // layout viewport bottom to reach the actual screen edge. Negative `bottom`
  // values are valid CSS and do exactly that. No Math.max clamp here.
  useEffect(() => {
    if (!visible) return;
    const vv = window.visualViewport;
    if (!vv) return;

    const updateBottom = () => {
      if (!containerRef.current) return;
      const offset = window.innerHeight - (vv.offsetTop + vv.height);
      containerRef.current.style.bottom = `${offset}px`;
    };

    updateBottom();
    vv.addEventListener("resize", updateBottom);
    // scroll fires when offsetTop changes (URL bar hide on WebKit) without resize
    vv.addEventListener("scroll", updateBottom);
    // window.resize fires when innerHeight changes instead of vv.height
    window.addEventListener("resize", updateBottom);
    return () => {
      vv.removeEventListener("resize", updateBottom);
      vv.removeEventListener("scroll", updateBottom);
      window.removeEventListener("resize", updateBottom);
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 px-4 py-3 shadow-[0_-4px_24px_rgba(15,23,42,0.08)] backdrop-blur-sm"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="mx-auto w-full max-w-lg">
        <TrackedWebAppLink
          href="https://app.briefpanel.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground shadow-md transition hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          location="sticky_mobile"
          label={t("cta")}
        >
          {t("cta")}
          <ArrowRight className="h-5 w-5" />
        </TrackedWebAppLink>
      </div>
    </div>
  );
};

export default StickyMobileCta;
